import {
    Component,
    ElementRef,
    ViewEncapsulation,
    computed,
    effect,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import {
    DisponibilidadePrivacidade,
    PrivacidadePublicaService,
} from 'src/app/shared/privacidade/privacidade-publica.service';

/** Guarda a versão publicada da Política de Cookies de que a pessoa tomou ciência. */
const CHAVE = 'privacidade_cookies_ciencia';
const VARIAVEL_ALTURA = '--altura-banner-cookies';
const CLASSE_COM_BANNER = 'com-banner-cookies';

/**
 * Aviso global de cookies/armazenamento. Só aparece com a parte pública do aluno no ar e enquanto a
 * versão publicada da Política de Cookies for diferente da que a pessoa já marcou com "Entendi"
 * (uma versão nova volta a mostrar o banner).
 * Reserva a própria altura: o `ion-tabs` raiz (que contém todas as páginas, cada uma com o seu
 * `ion-content` rolando por dentro, e a barra de abas) termina acima do banner, então nada fica
 * coberto. Alertas, toasts e modais do Ionic (z-index 20000+) continuam por cima dele.
 */
@Component({
    selector: 'app-banner-cookies',
    standalone: true,
    imports: [RouterLink],
    encapsulation: ViewEncapsulation.None,
    styles: `
        html.com-banner-cookies app-root > ion-tabs {
            bottom: var(--altura-banner-cookies);
            height: auto;
            /* A área segura de baixo (iPhone com notch) fica com o banner, não com as telas. */
            --ion-safe-area-bottom: 0px;
        }
    `,
    template: `
        @if (visivel()) {
        <div
            #banner
            class="fixed inset-x-0 bottom-0 z-10 bg-gray-900 text-white shadow-lg px-4 pt-3"
            style="padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px))"
            role="region"
            aria-label="Aviso de cookies"
        >
            <div class="max-w-4xl mx-auto flex flex-col md:flex-row md:items-center gap-2">
                <p class="text-sm m-0 flex-grow">
                    Este portal e o aplicativo guardam no aparelho a sessão, preferências (como o
                    tema), o RA digitado no login e a foto de perfil, que continuam lá depois de
                    sair. Usamos serviços de terceiros necessários ao funcionamento (fontes, ícones,
                    vídeos, consulta de CEP e notificações do app). Não usamos rastreamento,
                    analytics nem publicidade.
                    <a class="text-white underline" routerLink="/privacidade" fragment="cookies"
                        >Política de cookies</a
                    >
                </p>
                <button
                    type="button"
                    class="self-end md:self-auto px-4 py-2 rounded-lg bg-white text-gray-900 text-sm font-medium"
                    (click)="ciente()"
                >
                    Entendi
                </button>
            </div>
        </div>
        }
    `,
})
export class BannerCookiesComponent {
    private readonly estado = signal<DisponibilidadePrivacidade | null>(null);
    private readonly versaoCiente = signal(localStorage.getItem(CHAVE));
    private readonly banner = viewChild<ElementRef<HTMLElement>>('banner');

    /** Sem `versao_cookies` não há o que registrar, e o banner fica oculto. */
    visivel = computed(() => {
        const estado = this.estado();
        return (
            !!estado?.disponivel &&
            estado.versao_cookies !== null &&
            String(estado.versao_cookies) !== this.versaoCiente()
        );
    });

    constructor() {
        // Consulta leve, uma vez por carregamento do app.
        inject(PrivacidadePublicaService)
            .disponibilidade()
            .subscribe({
                next: estado => this.estado.set(estado),
                // Sem resposta, o banner fica oculto e volta a tentar no próximo carregamento.
                error: (erro: HttpErrorResponse) =>
                    console.warn(
                        '[BannerCookies] Disponibilidade do Portal de Privacidade indisponível; banner oculto.',
                        { status: erro.status, mensagem: erro.message },
                    ),
            });
        effect(onCleanup => {
            const elemento = this.banner()?.nativeElement;
            if (!elemento) {
                return;
            }
            const raiz = document.documentElement;
            const observador = new ResizeObserver(() =>
                raiz.style.setProperty(VARIAVEL_ALTURA, `${elemento.offsetHeight}px`),
            );
            observador.observe(elemento);
            raiz.classList.add(CLASSE_COM_BANNER);
            onCleanup(() => {
                observador.disconnect();
                raiz.classList.remove(CLASSE_COM_BANNER);
                raiz.style.removeProperty(VARIAVEL_ALTURA);
            });
        });
    }

    ciente(): void {
        const versao = String(this.estado()?.versao_cookies);
        // Esconde primeiro; depois registra a ciência desta versão.
        this.versaoCiente.set(versao);
        localStorage.setItem(CHAVE, versao);
    }
}
